<script setup lang="ts">
import type { ArchiveMonth, Category } from '~/types/blog'
import { normalizeFieldText } from '~/composables/useCustomFields'
import { buildPostPath } from '~/composables/usePermalink'

const route = useRoute()
const blogApi = useBlogApi()
const siteData = await useSiteData()
const siteBaseUrl = resolveSiteBaseUrl(siteData?.setting?.customFields)
const fields = siteData?.setting?.customFields
const { data: categories } = await useAsyncData(
  'archives:categories',
  () => blogApi.getCategoryTree()
)
const selectedCategory = ref(0)

// flattenCategories 保留层级顺序，供分类选项和子分类匹配复用
const flattenCategories = (nodes: Category[], depth = 0): { category: Category; depth: number }[] =>
  nodes.flatMap(category => [
    { category, depth },
    ...flattenCategories(category.children || [], depth + 1)
  ])

const categoryOptions = computed(() => flattenCategories(categories.value || []))
const filterOptions = computed(() => [
  { value: 0, label: '全部分类', depth: 0 },
  ...categoryOptions.value.map(({ category, depth }) => ({ value: category.ID, label: category.name, depth }))
].map(option => ({ ...option, icon: 'fa-regular fa-folder' })))
const selectedCategoryIDs = computed(() => {
  const option = categoryOptions.value.find(item => item.category.ID === selectedCategory.value)
  return new Set(option ? flattenCategories([option.category]).map(item => item.category.ID) : [])
})

const { data: archives } = await useAsyncData('archives', () => blogApi.getArchives())
const results = ref<HTMLElement | null>(null)
const filterTransitioning = ref(false)

// beforeFilterLeave 保留旧列表高度，避免离场时内容塌陷
const beforeFilterLeave = () => {
  if (!results.value) return
  results.value.style.height = `${results.value.offsetHeight}px`
  filterTransitioning.value = true
}

// enterFilterResults 将容器高度平滑过渡到新列表高度
const enterFilterResults = (element: Element) => {
  if (!results.value) return
  results.value.style.height = `${(element as HTMLElement).offsetHeight}px`
}

// finishFilterTransition 恢复自然高度
const finishFilterTransition = () => {
  if (results.value) results.value.style.height = ''
  filterTransitioning.value = false
}

// years 按选中分类过滤文章，再将归档月份合并为年度分组
const years = computed(() => {
  const groups = new Map<string, { year: string; items: ArchiveMonth['items'] }>()
  for (const month of archives.value || []) {
    const items = selectedCategory.value
      ? month.items.filter(post => post.categories?.some(category => selectedCategoryIDs.value.has(category.ID)))
      : month.items
    if (!items.length) continue
    const year = month.month.slice(0, 4)
    let group = groups.get(year)
    if (!group) {
      group = { year, items: [] }
      groups.set(year, group)
    }
    group.items.push(...items)
  }
  return Array.from(groups.values())
})

const siteName = normalizeFieldText(siteData?.setting?.customFields, 'siteName')
const pageDesc = normalizeFieldText(siteData?.setting?.customFields, 'archivesPageDescription', '按时间回溯文章内容。')

useSeoMeta({
  title: `归档 - ${siteName}`,
  description: pageDesc,
  ogTitle: `归档 - ${siteName}`,
  ogDescription: pageDesc
})

useHead({
  link: [{ rel: 'canonical', href: `${siteBaseUrl}${route.path}` }]
})
</script>

<template>
  <section class="archives-page">
    <div class="card hero">
      <h1>归档</h1>
      <div class="hero-description">
        <p>{{ pageDesc }}</p>
        <BlogDropdown
          :model-value="selectedCategory"
          :options="filterOptions"
          class="archive-filter"
          label="筛选归档分类"
          icon="fa-regular fa-folder"
          :show-arrow="false"
          @update:model-value="selectedCategory = Number($event)"
        />
      </div>
    </div>

    <div class="card archive-content">
      <div ref="results" class="archive-results" :class="{ 'is-changing': filterTransitioning }">
        <Transition
          name="archive-filter"
          mode="out-in"
          @before-leave="beforeFilterLeave"
          @enter="enterFilterResults"
          @after-enter="finishFilterTransition"
          @enter-cancelled="finishFilterTransition"
        >
          <div :key="selectedCategory">
            <ul v-if="years.length" class="year-list">
              <li v-for="item in years" :key="item.year" class="year-group" :aria-labelledby="`archive-${item.year}`">
                <div class="year-row">
                  <h2 :id="`archive-${item.year}`" class="year-title">{{ item.year }}</h2>
                  <NuxtLink
                    class="year-link"
                    :to="`/archives/year/${item.year}`"
                    :title="`查看 ${item.year} 年归档列表`"
                    :aria-label="`查看 ${item.year} 年归档列表`"
                  >
                    <BlogNavigationArrow direction="next" />
                  </NuxtLink>
                </div>
                <ul class="post-list">
                  <li v-for="post in item.items" :key="post.id">
                    <time v-if="post.publishedAt" :datetime="post.publishedAt">
                      {{ new Date(post.publishedAt).toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' }) }}
                    </time>
                    <div class="post-content">
                      <NuxtLink :to="buildPostPath(fields, { ID: post.id, slug: post.slug, publishedAt: post.publishedAt }, { category: post.categories?.[0]?.slug || '' })">
                        <span class="post-title" :title="post.title || post.slug">{{ post.title || post.slug }}</span>
                        <span v-if="post.categories?.length" class="post-meta">
                          <span v-for="category in post.categories" :key="category.ID" :title="category.name">{{ category.name }}</span>
                        </span>
                      </NuxtLink>
                    </div>
                  </li>
                </ul>
              </li>
            </ul>
            <div v-else class="archive-empty" role="status">
              <i class="fa-regular fa-folder-open" aria-hidden="true" />
              <p>{{ selectedCategory ? '该分类下暂无文章' : '暂无归档' }}</p>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </section>
</template>

<style scoped>
.archives-page {
  display: grid;
  gap: 14px;
}

.hero,
.archive-content {
  padding: 20px;
}

.hero-description {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  margin-top: 8px;
}

.archive-filter {
  --dropdown-padding: 0;
  margin-left: auto;
  line-height: 1.72;
}

.archive-results {
  transition: height 0.2s ease;
}

.archive-results.is-changing {
  overflow: clip;
}

.archive-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 180px;
  padding: 24px;
  color: var(--text-soft);
  font-size: 14px;
  text-align: center;
}

.archive-empty > i {
  font-size: 2rem;
  opacity: 0.4;
}

.archive-content {
  min-width: 0;
}

h1 {
  font-size: var(--heading-title-size);
}

.hero p {
  color: var(--text-soft);
}

.year-list {
  margin: 0;
  padding: 0;
  display: grid;
  gap: 32px;
  list-style: none;
}

.year-group {
  min-width: 0;
  isolation: isolate;
}

.year-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  font-size: min(1.5em, 5vw);
}

.year-title {
  --year-title-color: hsl(0 0% 70%);
  position: relative;
  z-index: -1;
  display: block;
  margin: 0 0 -0.3em;
  opacity: 0.5;
  color: transparent;
  -webkit-text-stroke: 1px var(--year-title-color);
  font-family: var(--font-number-outline);
  font-size: 3em;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  white-space: nowrap;
  mask-image: linear-gradient(#fff 30%, transparent 95%);
  transition: color 0.2s ease, -webkit-text-stroke-color 0.2s ease;
}

.year-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  color: var(--text-soft);
  font-size: 1.25rem;
  transition: color 0.2s ease;
}

.year-link:focus-visible {
  color: var(--brand);
}

.dark .year-title {
  --year-title-color: hsl(0 0% 50%);
}

.year-group:has(:focus-visible) .year-title {
  color: var(--year-title-color);
  -webkit-text-stroke-color: transparent;
}

.post-list {
  margin: 0;
  padding: 0;
  display: grid;
  list-style: none;
}

.post-list li {
  display: flex;
  align-items: center;
  gap: 0.5em;
  min-width: 0;
}

.post-content {
  position: relative;
  z-index: 0;
  flex: 1;
  min-width: 0;
  border-radius: 0.5rem;
}

.post-content::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;
  border: 2px solid var(--line);
  border-radius: inherit;
  background-color: var(--nav-item-hover-bg);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

.post-content a {
  display: flex;
  align-items: center;
  gap: 1em;
  padding: 0.3em 0.6em;
  border-radius: inherit;
  color: var(--text-soft);
  text-decoration: none;
  white-space: nowrap;
}

.post-list li:has(:focus-visible) .post-title {
  color: var(--text);
}

.post-title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.2s ease;
}

.post-meta {
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 45%;
  gap: 0.5em;
  margin-left: auto;
  font-size: 0.8em;
  opacity: 0.4;
  transition: opacity 0.2s ease;
}

.post-meta::before {
  content: '#';
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  line-height: 1;
  opacity: 0.5;
}

.post-meta > span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.post-list time {
  flex: 0 0 5ch;
  font-variant-numeric: tabular-nums;
  color: var(--text);
  opacity: 0.4;
  transition: opacity 0.2s ease;
}

.post-list li:has(:focus-visible) .post-content::before,
.post-list li:has(:focus-visible) .post-meta,
.post-list li:has(:focus-visible) time {
  opacity: 1;
}

.archive-filter-enter-active,
.archive-filter-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.archive-filter-enter-from,
.archive-filter-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

@media (hover: hover) and (pointer: fine) {
  .year-link:hover {
    color: var(--brand);
  }

  .year-group:hover .year-title {
    color: var(--year-title-color);
    -webkit-text-stroke-color: transparent;
  }

  .post-list li:hover .post-content::before,
  .post-list li:hover .post-meta,
  .post-list li:hover time {
    opacity: 1;
  }

  .post-list li:hover .post-title {
    color: var(--text);
  }
}

@media (prefers-reduced-motion: reduce) {
  .archive-results,
  .archive-filter-enter-active,
  .archive-filter-leave-active {
    transition: none;
  }
}

@media (max-width: 768px) {
  .post-list {
    font-size: 0.9em;
  }
}
</style>
